import type { HiveRoomState } from "@br9704/aethereum-shared";

/**
 * `aethereum watch` view model — pure derivation from room state to plain data.
 *
 * Everything renderable lives here so the Ink component stays a dumb painter:
 * presence liveness, the contract dependency tree (forward + reverse edges),
 * collision flash windows, and the bounded activity feed. Tested directly
 * (`model.test.ts`); the Ink layer is not.
 */

/** Presence is "live" while lastSeen is within this window (heartbeat is 30s). */
export const LIVE_WINDOW_MS = 90_000;
/** How many activity rows the feed keeps (newest at the bottom). */
export const ACTIVITY_LIMIT = 12;
/** How long a changed contract stays amber after a collision. */
export const FLASH_MS = 3_000;

export type WatchStatus = "connecting" | "sse" | "polling" | "reconnecting";

export interface WatchAgent {
  handle: string;
  live: boolean;
  /** What they're building — presence.building, falling back to latest intent. */
  building?: string;
}

export interface WatchContract {
  name: string;
  version: number;
  shape: string;
  /** Change-risk flag (stable/unstable/frozen) when the owner set one. */
  stability?: "stable" | "unstable" | "frozen";
  ownerHandle?: string;
  dependsOn: string[];
  /** Reverse edges: contracts whose dependsOn names this one. */
  dependedOnBy: string[];
  /** True while inside the post-collision flash window — render amber. */
  flashing: boolean;
}

export interface WatchActivityRow {
  /** HH:MM:SS, local time, from event.ts. */
  time: string;
  kind: "intent" | "contract" | "collision" | "chat" | "message" | "decision" | "plan" | "claim" | "proposal" | "directive" | "ticket" | "brief" | "goal" | "verification";
  /** Display handle — absent for collision rows (no memberId on the event). */
  handle?: string;
  text: string;
}

/** The standing operator order pinned to the top of the view (null = none). */
export interface WatchDirective {
  text: string;
  by?: string;
  /** Agent handles the directive is aimed at (absent = the whole room). */
  targets?: string[];
}

/** A shared-plan step, plus the rolled-up progress for the panel header. */
export interface WatchPlanStep {
  text: string;
  status: "todo" | "doing" | "done";
}
export interface WatchPlan {
  steps: WatchPlanStep[];
  done: number;
  total: number;
  /** 0..100, rounded — for the "x% done" header. */
  percent: number;
}

export interface WatchClaim {
  area: string;
  handle: string;
  /** True when another live agent also holds this area (coordinate or split). */
  overlap: boolean;
}

export interface WatchProposal {
  name: string;
  proposerHandle: string;
  oldShape: string;
  newShape: string;
  /** accept / push-back tally so far. */
  accepts: number;
  pushbacks: number;
  /** How many dependents still owe a verdict. */
  pending: number;
}

export interface WatchDecision {
  handle: string;
  text: string;
  tags: string[];
}

export interface WatchTicket {
  /** Short (8-char) display id. */
  ticketId: string;
  title: string;
  status: "open" | "doing" | "done" | "closed";
  assignee?: string;
  priority?: "low" | "med" | "high";
  /** Repeat cadence when the ticket is recurring. */
  recur?: "daily" | "weekly";
}

/** The team's current project brief (the shared blueprint), or null when unset. */
export interface WatchBrief {
  text: string;
  by?: string;
}

/** The active goal (the loop's objective + budget), or null when unset. */
export interface WatchGoal {
  goalId: string;
  text: string;
  status: "active" | "blocked" | "done" | "abandoned";
  maxIterations?: number;
  iteration?: number;
  budgetTokens?: number;
  spentTokens?: number;
  stopCondition?: string;
  by?: string;
}

/** A verification result (the loop's exit signal). */
export interface WatchVerification {
  target?: string;
  status: "pass" | "fail" | "partial";
  score?: number;
  summary: string;
  by?: string;
}

export interface WatchModel {
  /** The team's current project brief (the shared blueprint), or null. */
  brief: WatchBrief | null;
  /** The active goal (the loop's objective + budget), or null. */
  goal: WatchGoal | null;
  /** Latest verification per target (the loop's exit signal). */
  verifications: WatchVerification[];
  /** The active standing directive every agent must follow (null = none). */
  directive: WatchDirective | null;
  agents: WatchAgent[];
  contracts: WatchContract[];
  /** The team's shared checklist, with progress. Undefined when no plan is set. */
  plan?: WatchPlan;
  /** Advisory area claims ("route around these"). */
  claims: WatchClaim[];
  /** Open contract proposals awaiting dependent verdicts. */
  proposals: WatchProposal[];
  /** Durable team decisions on record (most recent first). */
  decisions: WatchDecision[];
  /** The team's active tickets (everything not `closed`), sorted for pickup. */
  tickets: WatchTicket[];
  activity: WatchActivityRow[];
  status: WatchStatus;
  eventCount: number;
  cursor: number;
}

export interface WatchInputs {
  /** contract name → flash expiry (epoch ms). Entries past `now` are settled. */
  flashes: ReadonlyMap<string, number>;
  status: WatchStatus;
  now: number;
  /** memberId → display handle (the stream hello roster). */
  roster?: Record<string, string>;
  /** Last seen stream cursor (event id), for the footer. */
  cursor?: number;
}

function handleFor(memberId: string, roster: Record<string, string> | undefined): string {
  return roster?.[memberId] ?? memberId.slice(0, 8);
}

export function formatClock(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function buildWatchModel(state: HiveRoomState, inputs: WatchInputs): WatchModel {
  const { flashes, status, now, roster } = inputs;
  const ctx = state.getContext(); // no memberId — read-only peek, cursor untouched

  // ── agents: union of presence + intents, presence wins on "building" ──────
  const agents = new Map<string, WatchAgent>();
  for (const p of ctx.presence) {
    agents.set(p.memberId, {
      handle: handleFor(p.memberId, roster),
      live: now - p.lastSeen < LIVE_WINDOW_MS,
      building: p.building,
    });
  }
  for (const i of ctx.intents) {
    const existing = agents.get(i.memberId);
    if (existing) {
      existing.building ??= i.text;
    } else {
      agents.set(i.memberId, { handle: handleFor(i.memberId, roster), live: false, building: i.text });
    }
  }

  // ── contracts: forward deps from the contract, reverse deps from everyone ──
  const contracts: WatchContract[] = ctx.contracts.map((c) => ({
    name: c.name,
    version: c.version,
    shape: c.shape,
    stability: c.stability,
    ownerHandle: c.ownerMemberId ? handleFor(c.ownerMemberId, roster) : undefined,
    dependsOn: c.dependsOn ?? [],
    dependedOnBy: ctx.contracts.filter((o) => o.dependsOn?.includes(c.name)).map((o) => o.name),
    flashing: (flashes.get(c.name) ?? 0) > now,
  }));

  // ── brief: the team's shared blueprint, pinned to the top of the view ─────
  const brief: WatchBrief | null = ctx.brief?.text
    ? { text: ctx.brief.text, by: ctx.brief.by ? handleFor(ctx.brief.by, roster) : undefined }
    : null;

  // ── goal: the loop's objective + budget, pinned near the top ──────────────
  const goal: WatchGoal | null = ctx.activeGoal
    ? {
        goalId: ctx.activeGoal.goalId,
        text: ctx.activeGoal.text,
        status: ctx.activeGoal.status,
        maxIterations: ctx.activeGoal.maxIterations,
        iteration: ctx.activeGoal.iteration,
        budgetTokens: ctx.activeGoal.budgetTokens,
        spentTokens: ctx.activeGoal.spentTokens,
        stopCondition: ctx.activeGoal.stopCondition,
        by: ctx.activeGoal.by ? handleFor(ctx.activeGoal.by, roster) : undefined,
      }
    : null;

  // ── verifications: the loop's exit signal (latest per target) ─────────────
  const verifications: WatchVerification[] = (ctx.verifications ?? []).map((v) => ({
    target: v.target,
    status: v.status,
    score: v.score,
    summary: v.summary,
    by: v.by ? handleFor(v.by, roster) : undefined,
  }));

  // ── directive: the standing operator order, pinned to the top of the view ─
  const directive: WatchDirective | null = ctx.activeDirective
    ? {
        text: ctx.activeDirective.text,
        by: ctx.activeDirective.by ? handleFor(ctx.activeDirective.by, roster) : undefined,
        targets: ctx.activeDirective.targets?.length ? ctx.activeDirective.targets : undefined,
      }
    : null;

  // ── shared plan: ordered checklist + rolled-up progress for the header ────
  let plan: WatchPlan | undefined;
  if (ctx.plan && ctx.plan.steps.length > 0) {
    const steps = ctx.plan.steps.map((s) => ({ text: s.text, status: s.status }));
    const total = steps.length;
    const done = steps.filter((s) => s.status === "done").length;
    plan = { steps, done, total, percent: Math.round((done / total) * 100) };
  }

  // ── claims: advisory area locks, flagged when another holder overlaps ─────
  const claimList = ctx.activeClaims ?? [];
  const areaCounts = new Map<string, number>();
  for (const c of claimList) areaCounts.set(c.area, (areaCounts.get(c.area) ?? 0) + 1);
  const claims: WatchClaim[] = claimList.map((c) => ({
    area: c.area,
    handle: handleFor(c.memberId, roster),
    overlap: (areaCounts.get(c.area) ?? 0) > 1,
  }));

  // ── proposals: open contract negotiations + their response tally ──────────
  const proposals: WatchProposal[] = state.openProposals().map((p) => ({
    name: p.name,
    proposerHandle: handleFor(p.proposerId, roster),
    oldShape: p.oldShape,
    newShape: p.newShape,
    accepts: p.responses.filter((r) => r.verdict === "accepted").length,
    pushbacks: p.responses.filter((r) => r.verdict !== "accepted").length,
    pending: p.pending.length,
  }));

  // ── decisions: durable team memory, most recent first ─────────────────────
  const decisions: WatchDecision[] = (ctx.decisions ?? []).map((d) => ({
    handle: handleFor(d.memberId, roster),
    text: d.text,
    tags: d.tags ?? [],
  }));

  // ── tickets: the active backlog (everything not closed), sorted for pickup ─
  const tickets: WatchTicket[] = (ctx.tickets ?? []).map((t) => ({
    ticketId: t.ticketId.length > 8 ? t.ticketId.slice(0, 8) : t.ticketId,
    title: t.title,
    status: t.status,
    assignee: t.assignee,
    priority: t.priority,
    recur: t.recur,
  }));

  // ── activity: bounded feed from the room log, newest at the bottom ────────
  const events = state.eventsSince(0);
  const rows: WatchActivityRow[] = [];
  for (const e of events) {
    switch (e.type) {
      case "intent":
        rows.push({
          time: formatClock(e.ts),
          kind: "intent",
          handle: handleFor(e.memberId, roster),
          text: `intent: ${e.text}`,
        });
        break;
      case "contract":
        rows.push({
          time: formatClock(e.ts),
          kind: "contract",
          handle: handleFor(e.memberId, roster),
          text: `declared ${e.name} v${e.version}`,
        });
        break;
      case "contract_changed":
        rows.push({
          time: formatClock(e.ts),
          kind: "collision",
          text: `${e.name} v${e.from}→v${e.to}`,
        });
        break;
      case "chat":
        rows.push({
          time: formatClock(e.ts),
          kind: "chat",
          handle: handleFor(e.memberId, roster),
          text: e.text,
        });
        break;
      case "message":
        rows.push({
          time: formatClock(e.ts),
          kind: "message",
          handle: handleFor(e.from, roster),
          text: `→ ${e.to === "all" ? "everyone" : handleFor(e.to, roster)}: ${e.text}`,
        });
        break;
      case "decision":
        rows.push({
          time: formatClock(e.ts),
          kind: "decision",
          handle: handleFor(e.memberId, roster),
          text: `decided: ${e.text}`,
        });
        break;
      case "brief":
        rows.push({
          time: formatClock(e.ts),
          kind: "brief",
          handle: e.by ? handleFor(e.by, roster) : undefined,
          text: "set the project brief",
        });
        break;
      case "plan": {
        const done = e.steps.filter((s) => s.status === "done").length;
        rows.push({
          time: formatClock(e.ts),
          kind: "plan",
          handle: handleFor(e.memberId, roster),
          text: `shared a plan (${e.steps.length} steps, ${done} done)`,
        });
        break;
      }
      case "claim":
        rows.push({
          time: formatClock(e.ts),
          kind: "claim",
          handle: handleFor(e.memberId, roster),
          text: e.status === "held" ? `claimed ${e.area}` : `released ${e.area}`,
        });
        break;
      case "contract_proposed":
        rows.push({
          time: formatClock(e.ts),
          kind: "proposal",
          handle: handleFor(e.memberId, roster),
          text: `proposed ${e.name}`,
        });
        break;
      case "proposal_response":
        rows.push({
          time: formatClock(e.ts),
          kind: "proposal",
          handle: handleFor(e.memberId, roster),
          text: `${e.verdict} a proposal${e.reason ? `: ${e.reason}` : ""}`,
        });
        break;
      case "contract_finalized":
        rows.push({
          time: formatClock(e.ts),
          kind: "proposal",
          handle: handleFor(e.memberId, roster),
          text:
            e.status === "finalized"
              ? `finalized ${e.name} v${e.version ?? "?"}`
              : e.status === "pending_approval"
                ? `${e.name} change parked for human approval`
                : e.status === "rejected"
                  ? `${e.name} change rejected by a human`
                  : `withdrew a proposal`,
        });
        break;
      case "directive":
        rows.push({
          time: formatClock(e.ts),
          kind: "directive",
          handle: e.by ? handleFor(e.by, roster) : "lead",
          text: e.cleared ? "cleared the team directive" : `⚡ directive: ${e.text}`,
        });
        break;
      case "ticket": {
        const verb = e.status === "closed" || e.status === "done" || e.status === "doing" ? "updated" : "created";
        rows.push({
          time: formatClock(e.ts),
          kind: "ticket",
          handle: e.by,
          text: `${verb} ticket "${e.title}" [${e.status}]`,
        });
        break;
      }
      case "goal":
        rows.push({
          time: formatClock(e.ts),
          kind: "goal",
          handle: e.by ? handleFor(e.by, roster) : undefined,
          text:
            e.status === "active"
              ? `◎ goal: ${e.text}`
              : `goal ${e.status}: ${e.text}`,
        });
        break;
      case "verification": {
        const icon = e.status === "pass" ? "✓" : e.status === "fail" ? "✗" : "~";
        rows.push({
          time: formatClock(e.ts),
          kind: "verification",
          handle: e.by ? handleFor(e.by, roster) : undefined,
          text: `${icon} verified ${e.target ?? "(room)"}: ${e.status} — ${e.summary}`,
        });
        break;
      }
      case "presence":
        break; // heartbeats are status, not activity
    }
  }

  return {
    brief,
    goal,
    verifications,
    directive,
    agents: [...agents.values()].sort((a, b) => a.handle.localeCompare(b.handle)),
    contracts,
    plan,
    claims,
    proposals,
    decisions,
    tickets,
    activity: rows.slice(-ACTIVITY_LIMIT),
    status,
    eventCount: events.length,
    cursor: inputs.cursor ?? ctx.cursor,
  };
}
